// ** React Imports
import { useState, useEffect } from 'react'

// ** Next Imports

// ** MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Snackbar from '@mui/material/Snackbar'
import {
  Alert,
  AlertTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { Save, Remove } from '@mui/icons-material'

// ** Custom Imports
import { jewelsData, levelCosts } from 'src/data/jewels'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Styled Components
import CustomHeader from 'src/@core/components/Header'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'
import { construirEnlace, leerEstadoDeUrl } from 'src/@core/utils/enlace-compartible'

/*
  Estado compartible. Lo que el jugador decide en /jewels/ son cuatro numeros por
  ranura —nivel actual y objetivo de cada una de las dos joyas—, y nada mas: las
  stats y los costes salen de jewelsData y levelCosts, que ya estan en el bundle.
*/
const aEstadoMinimo = config =>
  Object.fromEntries(
    Object.entries(config ?? {}).map(([id, c]) => [
      id,
      [c.jewel1?.currentLevel ?? 1, c.jewel1?.targetLevel ?? 2, c.jewel2?.currentLevel ?? 1, c.jewel2?.targetLevel ?? 2]
    ])
  )

const desdeEstadoMinimo = minimo => {
  if (!minimo || typeof minimo !== 'object') return null
  const base = defaultJewelConfig()
  let alguno = false

  for (const [id, v] of Object.entries(minimo)) {
    if (!base[id] || !Array.isArray(v) || v.length !== 4) continue
    // Recortado al rango de la tabla: un enlace tocado a mano no deja niveles imposibles
    const lim = n => Math.min(Math.max(Number(n) || 1, 1), levelCosts.length)
    base[id] = {
      jewel1: { currentLevel: lim(v[0]), targetLevel: lim(v[1]) },
      jewel2: { currentLevel: lim(v[2]), targetLevel: lim(v[3]) }
    }
    alguno = true
  }

  return alguno ? base : null
}

/*
  Tabla de referencia renderizada en el servidor. /jewels/ emitia 12 filas, y son
  las del formulario de la calculadora —Stat, Current, Target—, no datos del juego.
  La tabla de coste por nivel, que es lo que se consulta, no salia en el HTML.

  Se añade el acumulado porque la pregunta que se hace un jugador no es "que cuesta
  el nivel 31" sino "cuanto me falta para llegar al 31".
*/
const COLUMNAS_JOYAS = [
  { clave: 'level', encabezado: 'Jewel level', numerica: true },
  { clave: 'jewels', encabezado: 'Jewels for this level', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'essence', encabezado: 'Essence for this level', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'accJewels', encabezado: 'Jewels from level 1', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'accEssence', encabezado: 'Essence from level 1', numerica: true, formato: v => v.toLocaleString('en-US') }
]

const FILAS_JOYAS = (() => {
  let aj = 0
  let ae = 0

  return levelCosts.map(l => {
    aj += l.jewels ?? 0
    ae += l.essence ?? 0

    return { id: 'lvl-' + l.level, level: l.level, jewels: l.jewels ?? 0, essence: l.essence ?? 0, accJewels: aj, accEssence: ae }
  })
})()


// ** Styled Buttons (same pattern as other pages)
const GreenButton = styled(Button)(({ theme }) => ({
  // Verde oscuro y texto blanco: #E8F1F5 sobre #1B8A6B daba 3,74:1, por debajo de AA.
  backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
    filter: 'brightness(0.85)'
  }
}))

// ** Jewel color map for jewel-colored resource icon
// Colores de relleno de cada joya: van detras de texto blanco o como pastilla.
const jewelColorMap = {
  ruby: '#E53935',
  sapphire: '#1E88E5',
  topaz: '#F9A825'
}

/*
  Los mismos colores no sirven como TEXTO. Sobre blanco daban 4,22:1 el rubi,
  3,68:1 el zafiro y ~2:1 el topacio; sobre el papel del modo oscuro, 3,09 y
  3,55. Estas son las variantes legibles de cada uno, por modo, conservando el
  matiz que identifica a la joya.
*/
const jewelTextColorMap = {
  light: { ruby: '#C62828', sapphire: '#1565C0', topaz: '#8A5A00' },
  dark: { ruby: '#FF8A80', sapphire: '#6FA8FF', topaz: '#FFCA5F' }
}

// ** Calculate upgrade cost between two levels
const calcUpgradeCost = (fromLevel, toLevel) => {
  let totalJewels = 0
  let totalEssence = 0
  let hasUnknown = false

  for (let lvl = fromLevel + 1; lvl <= toLevel; lvl++) {
    const cost = levelCosts.find(c => c.level === lvl)
    if (!cost || cost.jewels === null) {
      hasUnknown = true
    } else {
      totalJewels += cost.jewels
      totalEssence += cost.essence
    }
  }

  return { totalJewels, totalEssence, hasUnknown }
}

// ** Default state for all 6 jewel slots (2 jewels each)
const defaultJewelConfig = () =>
  jewelsData.reduce((acc, jewel) => {
    acc[jewel.id] = {
      jewel1: { currentLevel: 1, targetLevel: 2 },
      jewel2: { currentLevel: 1, targetLevel: 2 }
    }

    return acc
  }, {})

// ** Single Jewel Slot Card matching the game UI (now shows 2 jewels per slot)
const JewelSlot = ({ jewel, config, onConfigChange }) => {
  const { jewel1, jewel2 } = config
  const fromStats1 = jewel.levels.find(l => l.level === jewel1.currentLevel)
  const toStats1 = jewel.levels.find(l => l.level === jewel1.targetLevel)
  const fromStats2 = jewel.levels.find(l => l.level === jewel2.currentLevel)
  const toStats2 = jewel.levels.find(l => l.level === jewel2.targetLevel)
  const cost1 = calcUpgradeCost(jewel1.currentLevel, jewel1.targetLevel)
  const cost2 = calcUpgradeCost(jewel2.currentLevel, jewel2.targetLevel)

  const totalCost = {
    totalJewels: cost1.totalJewels + cost2.totalJewels,
    totalEssence: cost1.totalEssence + cost2.totalEssence,
    hasUnknown: cost1.hasUnknown || cost2.hasUnknown
  }
  const jewelColor = jewelColorMap[jewel.type]
  // relleno vs texto: ver jewelTextColorMap
  const jewelTextColor = jewelTextColorMap[useTheme().palette.mode][jewel.type]

  return (
    <Box
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // background.paper y no #fff: la tarjeta quedaba blanca en modo oscuro
        backgroundColor: 'background.paper'
      }}
    >
      {/* Jewel header bar */}
      <Box
        sx={{
          background: `linear-gradient(90deg, ${jewelColor}, ${jewelColor}cc)`,
          padding: '8px 16px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: '#fff'
        }}
      >
        Jewel Upgrade (2x)
      </Box>

      {/* Jewel image + name */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem 1rem 0.5rem' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              border: `3px solid ${jewelColor}`,
              overflow: 'hidden',
              boxShadow: `0 0 16px ${jewelColor}55`
            }}
          >
            <Box
              component='img'
              src={jewel.image}
              alt={`${jewel.name} ${jewel.equipment} #1`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              border: `3px solid ${jewelColor}`,
              overflow: 'hidden',
              boxShadow: `0 0 16px ${jewelColor}55`
            }}
          >
            <Box
              component='img'
              src={jewel.image}
              alt={`${jewel.name} ${jewel.equipment} #2`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        </Box>
        <Typography variant='body1' sx={{ fontWeight: 'bold', color: jewelTextColor, mt: 1 }}>
          {jewel.name} — {jewel.equipment}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          2 Jewels per slot
        </Typography>
      </Box>

      {/* Level selectors for both jewels */}
      {[
        { label: 'Jewel #1', data: jewel1, key: 'jewel1' },
        { label: 'Jewel #2', data: jewel2, key: 'jewel2' }
      ].map(({ label, data, key }) => (
        <Box key={key} sx={{ padding: '0.5rem 1rem' }}>
          <Typography variant='caption' sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
            <TextField
              select
              size='small'
              value={data.currentLevel}
              onChange={e => {
                const val = Number(e.target.value)

                const newConfig = {
                  ...config,
                  [key]: {
                    ...data,
                    currentLevel: val,
                    targetLevel: val >= data.targetLevel ? Math.min(val + 1, 40) : data.targetLevel
                  }
                }
                onConfigChange(newConfig)
              }}
              label='From'
              sx={{ width: 90 }}
            >
              {Array.from({ length: 39 }, (_, i) => i + 1).map(lvl => (
                <MenuItem key={lvl} value={lvl}>
                  {lvl}
                </MenuItem>
              ))}
            </TextField>

            <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: jewelTextColor }}>▸▸</Typography>

            <TextField
              select
              size='small'
              value={data.targetLevel}
              onChange={e => {
                const newConfig = { ...config, [key]: { ...data, targetLevel: Number(e.target.value) } }
                onConfigChange(newConfig)
              }}
              label='To'
              sx={{ width: 90 }}
            >
              {Array.from({ length: 40 - data.currentLevel }, (_, i) => data.currentLevel + 1 + i).map(lvl => (
                <MenuItem key={lvl} value={lvl}>
                  {lvl}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      ))}

      {/* Combined Stats comparison */}
      <Box sx={{ padding: '0 1rem', flex: 1 }}>
        <Divider sx={{ my: 1 }} />
        <Typography
          variant='caption'
          sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5, textAlign: 'center' }}
        >
          Combined Stats (Both Jewels)
        </Typography>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '4px 6px' }}>Stat</TableCell>
                <TableCell align='right' sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '4px 6px' }}>
                  Current
                </TableCell>
                <TableCell align='center' sx={{ padding: '4px 2px' }}>
                  <Typography sx={{ color: jewelTextColor, fontSize: '0.7rem' }}>▸</Typography>
                </TableCell>
                <TableCell
                  align='right'
                  sx={{ color: 'customColors.successText', fontWeight: 'bold', fontSize: '0.7rem', padding: '4px 6px' }}
                >
                  Target
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jewel.statNames.map((statName, i) => {
                const fromVal =
                  (fromStats1 ? fromStats1[`stat${i + 1}`] : 0) + (fromStats2 ? fromStats2[`stat${i + 1}`] : 0)
                const toVal = (toStats1 ? toStats1[`stat${i + 1}`] : 0) + (toStats2 ? toStats2[`stat${i + 1}`] : 0)

                return (
                  <TableRow key={i}>
                    <TableCell sx={{ fontSize: '0.72rem', padding: '5px 6px' }}>
                      {statName}
                      {jewel.statNotes[i] && (
                        <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', fontSize: '0.6rem' }}>
                          {jewel.statNotes[i]}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align='right' sx={{ fontSize: '0.75rem', padding: '5px 6px' }}>
                      {parseFloat(fromVal.toFixed(1))}
                    </TableCell>
                    <TableCell align='center' sx={{ padding: '5px 2px' }}>
                      <Typography sx={{ color: jewelTextColor, fontSize: '0.7rem' }}>▸</Typography>
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{ color: 'customColors.successText', fontWeight: 'bold', fontSize: '0.75rem', padding: '5px 6px' }}
                    >
                      {parseFloat(toVal.toFixed(1))}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Resource cost bar at bottom */}
      <Box
        sx={{
          marginTop: 'auto',
          padding: '0.75rem 1rem',
          backgroundColor: 'action.hover',
          borderTop: theme => `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          alignItems: 'center'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: jewelColor }} />
            <Typography variant='body2' sx={{ fontWeight: 'bold', color: jewelTextColor }}>
              {totalCost.totalJewels.toLocaleString()}
            </Typography>
          </Box>
          <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
            {jewel.name} Fragment
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: '#9C27B0' }} />
            <Typography variant='body2' sx={{ fontWeight: 'bold', color: '#7B1FA2' }}>
              {totalCost.totalEssence.toLocaleString()}
            </Typography>
          </Box>
          <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
            Essence
          </Typography>
        </Box>
      </Box>

      {totalCost.hasUnknown && (
        <Alert severity='warning' sx={{ borderRadius: 0, fontSize: '0.7rem', padding: '2px 8px' }}>
          Some levels have unknown costs.
        </Alert>
      )}
    </Box>
  )
}

// ** Main Component
export default function Jewels() {
  const [jewelConfig, setJewelConfig] = useState(defaultJewelConfig())
  const [selectedSlot, setSelectedSlot] = useState(1)
  const [snackOpen, setSnackOpen] = useState({ open: false, type: 'success', time: undefined })

  // Load from localStorage on mount
  useEffect(() => {
    /*
      El enlace compartido gana sobre lo guardado: quien abre el enlace de otro
      quiere ver esa configuracion. Lo propio sigue en localStorage y vuelve al
      entrar sin parametros.
    */
    const compartido = desdeEstadoMinimo(leerEstadoDeUrl())
    if (compartido) {
      setJewelConfig(compartido)

      return
    }

    if (typeof window !== 'undefined') {
      const saved = JSON.parse(localStorage.getItem('jewelConfig'))
      if (saved) {
        setJewelConfig(saved)
      }
    }
  }, [])

  const handleShare = async () => {
    const enlace = construirEnlace(aEstadoMinimo(jewelConfig))
    if (!enlace) return setSnackOpen({ open: 'Could not build the link.', type: 'error', time: 4000 })

    try {
      await navigator.clipboard.writeText(enlace)

      return setSnackOpen({ open: 'Link copied — paste it anywhere.', type: 'success', time: 4000 })
    } catch (e) {
      window.history.replaceState(null, '', enlace)

      return setSnackOpen({ open: 'Link is now in the address bar — copy it from there.', type: 'info', time: 6000 })
    }
  }

  const handleSaveData = () => {
    localStorage.setItem('jewelConfig', JSON.stringify(jewelConfig))
    setSnackOpen({ open: 'Information Saved.', type: 'success', time: 3000 })
  }

  const handleRemoveItems = () => {
    localStorage.removeItem('jewelConfig')
    setJewelConfig(defaultJewelConfig())
    setSnackOpen({ open: 'Information Cleared.', type: 'error', time: 3000 })
  }

  const snackClose = (event, reason) => {
    if (reason === 'clickaway') return
    setSnackOpen({ open: undefined, type: snackOpen.type, time: 3000 })
  }

  const updateJewelConfig = (jewelId, newConfig) => {
    setJewelConfig(prev => ({
      ...prev,
      [jewelId]: newConfig
    }))
  }

  const selectedJewel = jewelsData.find(j => j.id === selectedSlot)
  const selectedConfig = jewelConfig[selectedSlot]

  return (
    <>
      <Snackbar
        open={snackOpen.open ? true : false}
        autoHideDuration={snackOpen.time ?? 7000}
        onClose={snackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert variant='filled' onClose={snackClose} severity={snackOpen.type}>
          <AlertTitle>{snackOpen.type === 'success' ? 'Done!' : 'Info:'}</AlertTitle>
          {snackOpen.open}
        </Alert>
      </Snackbar>

      <Grid container spacing={6}>
        {/* Header + Controls merged */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <CustomHeader icon='jewels' title='JEWEL UPGRADE CALCULATOR' />
            <Divider sx={{ marginY: '1rem' }} />
            <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1rem', color: 'primary.main' }}>
              Calculate Jewel Upgrade Costs
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              The Jewel Upgrade Calculator shows the exact resources needed to upgrade your jewels from their current
              level to your desired target level in War and Order. Accurate cost forecasting helps you plan ahead before
              committing materials.
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', marginBottom: '1rem' }}>
              Select a jewel slot, enter your current and target levels, and instantly see the required upgrade
              materials. Plan your jewel investments wisely to avoid running short on rare resources.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: '1rem',
                marginBottom: '0.5rem',
                alignItems: 'center'
              }}
            >
              <GreenButton variant='contained' startIcon={<Icon icon='ic:baseline-save' />} onClick={handleSaveData}>
                SAVE
              </GreenButton>
              <Button variant='outlined' startIcon={<Icon icon='ic:baseline-link' />} onClick={handleShare}>
                SHARE
              </Button>
              <Button
                variant='contained'
                color='error'
                startIcon={<Icon icon='ic:baseline-delete' />}
                onClick={handleRemoveItems}
              >
                DELETE
              </Button>
            </Box>
            <Typography
              variant='caption'
              sx={{
                display: 'block',
                textAlign: 'right',
                color: 'text.secondary',
                fontSize: '0.75rem'
              }}
            >
              Created by DvdVgt
            </Typography>
          </Card>
        </Grid>

        {/* 6 Jewel Slot Selector - resembles game top bar */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ padding: '1rem' }}>
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
                display: 'block',
                textAlign: 'center',
                marginBottom: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: 1
              }}
            >
              Select Jewel Slot
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
              {jewelsData.map(jewel => {
                const isActive = selectedSlot === jewel.id
                const cfg = jewelConfig[jewel.id]

                return (
                  <Box
                    key={jewel.id}
                    onClick={() => setSelectedSlot(jewel.id)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <Typography
                      variant='caption'
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: isActive ? 'bold' : 'normal',
                        color: isActive ? 'primary.main' : 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}
                    >
                      {jewel.equipment}
                    </Typography>
                    <Box
                      sx={{
                        width: { xs: 56, md: 72 },
                        height: { xs: 56, md: 72 },
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: isActive ? `3px solid #FFD700` : '3px solid #e0e0e0',
                        boxShadow: isActive ? '0 0 12px rgba(255,215,0,0.4)' : '0 2px 4px rgba(0,0,0,0.1)',
                        position: 'relative',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: '#FFD700', transform: 'scale(1.05)' }
                      }}
                    >
                      <Box
                        component='img'
                        src={jewel.image}
                        alt={`${jewel.name} ${jewel.equipment}`}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {/* Level badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'rgba(0,0,0,0.75)',
                          textAlign: 'center',
                          padding: '1px 0'
                        }}
                      >
                        <Typography variant='caption' sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 'bold' }}>
                          Lv.{cfg?.jewel1?.currentLevel || 1}/{cfg?.jewel2?.currentLevel || 1}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Card>
        </Grid>

        {/* Selected jewel detail card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <JewelSlot
            jewel={selectedJewel}
            config={
              selectedConfig || {
                jewel1: { currentLevel: 1, targetLevel: 2 },
                jewel2: { currentLevel: 1, targetLevel: 2 }
              }
            }
            onConfigChange={newConfig => updateJewelConfig(selectedSlot, newConfig)}
          />
        </Grid>

        {/* All 6 jewels overview */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ padding: '1rem', height: '100%' }}>
            <Typography
              variant='body1'
              sx={{ fontWeight: 'bold', marginBottom: '1rem', textAlign: 'center', letterSpacing: 1 }}
            >
              ALL JEWELS OVERVIEW
            </Typography>
            <Divider sx={{ marginBottom: '1rem' }} />
            {/* tabIndex: la tabla desborda en horizontal y sin foco no se puede recorrer con teclado */}
            <TableContainer tabIndex={0}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px' }}>Slot</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px' }}>
                      Current
                    </TableCell>
                    <TableCell align='center' sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px' }}>
                      Target
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px' }}>
                      Fragments
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 'bold', fontSize: '0.7rem', padding: '6px' }}>
                      Essence
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jewelsData.map(jewel => {
                    const cfg = jewelConfig[jewel.id]
                    const cost1 = calcUpgradeCost(cfg?.jewel1?.currentLevel || 1, cfg?.jewel1?.targetLevel || 2)
                    const cost2 = calcUpgradeCost(cfg?.jewel2?.currentLevel || 1, cfg?.jewel2?.targetLevel || 2)
                    const totalFragments = cost1.totalJewels + cost2.totalJewels
                    const totalEss = cost1.totalEssence + cost2.totalEssence
                    const isSelected = selectedSlot === jewel.id

                    return (
                      <TableRow
                        key={jewel.id}
                        onClick={() => setSelectedSlot(jewel.id)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: isSelected ? 'action.selected' : 'transparent',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell sx={{ padding: '6px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: jewelColorMap[jewel.type]
                              }}
                            />
                            <Typography variant='caption' sx={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                              {jewel.name} ({jewel.equipment})
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align='center' sx={{ fontSize: '0.75rem', padding: '6px' }}>
                          {cfg?.jewel1?.currentLevel || 1}/{cfg?.jewel2?.currentLevel || 1}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ color: 'customColors.successText', fontWeight: 'bold', fontSize: '0.75rem', padding: '6px' }}
                        >
                          {cfg?.jewel1?.targetLevel || 2}/{cfg?.jewel2?.targetLevel || 2}
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{
                            color: theme => jewelTextColorMap[theme.palette.mode][jewel.type],
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            padding: '6px'
                          }}
                        >
                          {totalFragments.toLocaleString()}
                        </TableCell>
                        <TableCell align='right' sx={{ color: '#7B1FA2', fontSize: '0.75rem', padding: '6px' }}>
                          {totalEss.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                  {/* Total row */}
                  {(() => {
                    let totalJewelsRuby = 0,
                      totalJewelsSapphire = 0,
                      totalJewelsTopaz = 0,
                      totalEssence = 0
                    jewelsData.forEach(jewel => {
                      const cfg = jewelConfig[jewel.id]
                      const cost1 = calcUpgradeCost(cfg?.jewel1?.currentLevel || 1, cfg?.jewel1?.targetLevel || 2)
                      const cost2 = calcUpgradeCost(cfg?.jewel2?.currentLevel || 1, cfg?.jewel2?.targetLevel || 2)
                      const totalFragments = cost1.totalJewels + cost2.totalJewels
                      const totalEss = cost1.totalEssence + cost2.totalEssence
                      if (jewel.type === 'ruby') totalJewelsRuby += totalFragments
                      else if (jewel.type === 'sapphire') totalJewelsSapphire += totalFragments
                      else totalJewelsTopaz += totalFragments
                      totalEssence += totalEss
                    })

                    return (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            borderBottom: 'none',
                            padding: '8px 6px',
                            borderTop: theme => `2px solid ${theme.palette.divider}`
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ borderBottom: 'none', padding: '8px 6px', borderTop: theme => `2px solid ${theme.palette.divider}` }}
                        >
                          <Typography variant='caption' sx={{ color: theme => jewelTextColorMap[theme.palette.mode].ruby, display: 'block', fontWeight: 'bold' }}>
                            R: {totalJewelsRuby.toLocaleString()}
                          </Typography>
                          <Typography variant='caption' sx={{ color: theme => jewelTextColorMap[theme.palette.mode].sapphire, display: 'block', fontWeight: 'bold' }}>
                            S: {totalJewelsSapphire.toLocaleString()}
                          </Typography>
                          <Typography variant='caption' sx={{ color: theme => jewelTextColorMap[theme.palette.mode].topaz, display: 'block', fontWeight: 'bold' }}>
                            T: {totalJewelsTopaz.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{
                            color: '#7B1FA2',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            borderBottom: 'none',
                            padding: '8px 6px',
                            borderTop: theme => `2px solid ${theme.palette.divider}`
                          }}
                        >
                          {totalEssence.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    )
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
        <Grid size={{ xs: 12 }} sx={{ pt: 6 }}>
          <TablaReferencia
            titulo='Jewel upgrade cost by level, with the running total from level 1'
            columnas={COLUMNAS_JOYAS}
            filas={FILAS_JOYAS}
            idFila='id'
          />
        </Grid>
    </>
  )
}

Jewels.noLoadingGate = true
